import { useState } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { SIDE_BAR_STATUS } from 'constants/ui/storageKey';
import { SIDE_BAR_STATUS_ENUM } from 'constants/ui/status';
import { colors } from 'theme/mui/colors';
import { useSidebarItems } from 'hooks/services/useSidebarItems';
import { useWhitelabelSettings } from 'modules/branches/hooks';
import { AuthApi } from 'api';
import ClickableImage from '../image/ClickableImage';
import SidebarClosIcon from '../icons/SidebarCloseIcon';
import LogoSmallIcon from '../icons/LogoSmallIcon';
import { Z_INDEX_MOBILE_NAVBAR } from 'constants/ui/style';
import UserProfileBottomBar from './UserProfileBottomBar';
import TextTooltip from '../text/TextTooltip';
import useAppNavigation from 'hooks/general/useAppNavigation';
import { UNAVAILABLE_MOBILE_NAVBAR_ROUTES } from 'constants/ui/routes';

const Sidebar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentPath } = useAppNavigation();
  const { pathname, search } = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isSidebarClosed, setIsSidebarClosed] = useState(
    localStorage.getItem(SIDE_BAR_STATUS) === SIDE_BAR_STATUS_ENUM.CLOSED
  );
  const { mainSidebarItems, bottomSidebarItems, mobileNavItems } = useSidebarItems();
  const isMobileNavbarAvailable = !UNAVAILABLE_MOBILE_NAVBAR_ROUTES.includes(currentPath);

  const { data: whitelabel } = useWhitelabelSettings(AuthApi.isAuthenticated());

  const toggleSidebar = () => {
    const newStatus = isSidebarClosed ? SIDE_BAR_STATUS_ENUM.OPEN : SIDE_BAR_STATUS_ENUM.CLOSED;
    localStorage.setItem(SIDE_BAR_STATUS, newStatus);
    setIsSidebarClosed(!isSidebarClosed);
  };

  const isMenuItemActive = (route: string) => {
    // Handle root route
    if (route === '/') {
      return pathname === '/';
    }

    // Check if route contains query parameters
    if (route.includes('?')) {
      const [routePath, queryString] = route.split('?');
      const routeParams = new URLSearchParams(queryString);
      const currentParams = new URLSearchParams(search);
      // First check if the pathname matches
      if (!pathname.startsWith(routePath)) {
        return false;
      }
      // Then check if all route query parameters match current query parameters
      for (const [key, value] of routeParams.entries()) {
        if (currentParams.get(key) !== value) {
          return false;
        }
      }

      return true;
    }

    // For regular routes without query parameters
    return pathname.startsWith(route);
  };
  const handleNavigation = (route: string) => {
    navigate(route);
  };

  if (isMobile) {
    if (isMobileNavbarAvailable) {
      return (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-around',
            padding: '0.75rem',
            zIndex: Z_INDEX_MOBILE_NAVBAR,
            backgroundColor: 'background.paper',
          }}
        >
          {mobileNavItems.map((item, index) => (
            <Stack
              key={index + item.route}
              sx={{
                color: isMenuItemActive(item.route) ? colors.red[500] : colors.base.level60,
                gap: 0.5,
                fontSize: '1rem',
              }}
              alignItems="center"
              onClick={() => handleNavigation(item.route)}
            >
              {item.icon}
              <TextTooltip
                value={item.label}
                maxLength={10}
                textProps={{
                  variant: 'body1',
                  color: isMenuItemActive(item.route) ? colors.red[500] : colors.base.level60,
                }}
              />
            </Stack>
          ))}
        </Box>
      );
    }
    return <></>;
  }

  return (
    <Drawer
      variant="permanent"
      open={!isSidebarClosed}
      sx={{
        width: isSidebarClosed ? `4.5rem` : `220px`,
        flexShrink: 0,
        overflow: 'hidden',
        transition: 'width 0.3s ease-in-out',
        '& .MuiDrawer-paper': {
          width: isSidebarClosed ? `4.5rem` : `13.75rem`,
          boxSizing: 'border-box',
          position: 'fixed',
          overflow: 'auto',
        },
      }}
    >
      {/* Logo and Collapse Icon */}
      <Stack
        direction={isSidebarClosed ? 'column' : 'row'}
        sx={{ p: 2, alignItems: 'center', justifyContent: 'space-between' }}
        gap={1}
      >
        <ClickableImage
          height={24}
          imgSrc={isSidebarClosed ? <LogoSmallIcon /> : (whitelabel?.logoUrl ?? '/logo.png')}
          width="auto"
          path="/"
        />
        {!isSidebarClosed && (
          <IconButton aria-label="close-sidebar" onClick={toggleSidebar} sx={{ marginRight: -1 }}>
            <SidebarClosIcon />
          </IconButton>
        )}
      </Stack>

      {/* Main Menu Sidebar */}
      <List sx={{ flexGrow: 1, px: isSidebarClosed ? 0 : 1 }}>
        {mainSidebarItems.map((item, index) => (
          <ListItem key={item.route + index} disablePadding={!isSidebarClosed}>
            <ListItemButton
              component={RouterLink}
              to={item.route}
              sx={{
                borderRadius: 1,
                justifyContent: isSidebarClosed ? 'center' : 'initial',
                px: 1.5,
                alignItems: 'center',
                '&.Mui-selected': {
                  backgroundColor: isMenuItemActive(item.route) ? colors.gray[100] : 'transparent',
                },
              }}
              selected={isMenuItemActive(item.route)}
              onClick={() => handleNavigation(item.route)}
            >
              <Tooltip title={isSidebarClosed ? item.label : undefined} placement="right-end">
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isSidebarClosed ? 0 : 1,
                    justifyContent: 'center',
                    color: isMenuItemActive(item.route) ? colors.red[500] : 'initial',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              </Tooltip>
              {!isSidebarClosed && <ListItemText primary={item.label} />}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Bottom Menu Sidebar */}
      <List sx={{ px: isSidebarClosed ? 0 : 1 }}>
        {bottomSidebarItems.map((item, index) => (
          <ListItem key={index + item.route} disablePadding={!isSidebarClosed}>
            <ListItemButton
              component={RouterLink}
              to={item.route}
              sx={{
                borderRadius: 1,
                justifyContent: isSidebarClosed ? 'center' : 'initial',
                px: 1.5,
                alignItems: 'center',
                '&.Mui-selected': {
                  backgroundColor: isMenuItemActive(item.route) ? colors.gray[100] : 'transparent',
                },
                width: isSidebarClosed ? '2.5rem' : '100%',
              }}
              selected={isMenuItemActive(item.route)}
              onClick={() => handleNavigation(item.route)}
            >
              <Tooltip title={isSidebarClosed ? item.label : undefined} placement="right-end">
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isSidebarClosed ? 0 : 2,
                    justifyContent: 'center',
                    color: isMenuItemActive(item.route) ? colors.red[500] : 'initial',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              </Tooltip>
              {!isSidebarClosed && <ListItemText primary={item.label} />}
            </ListItemButton>
          </ListItem>
        ))}

        <Divider sx={{ m: 2 }} />
        {isSidebarClosed && (
          <ListItem disablePadding={!isSidebarClosed}>
            <IconButton aria-label="close-sidebar" onClick={toggleSidebar}>
              <SidebarClosIcon
                style={{
                  transform: 'rotate(180deg)',
                  transformOrigin: '50% 50%',
                }}
              />
            </IconButton>
          </ListItem>
        )}

        <UserProfileBottomBar isSidebarClosed={isSidebarClosed} />
      </List>
    </Drawer>
  );
};

export default Sidebar;
